import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import { useIdleTimer } from "react-idle-timer";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { AppTopbar } from "./AppTopbar";
import { AppMenu } from "./AppMenu";
import { AppProfile } from "./AppProfile";

import { Dashboard } from "./components/Dashboard";

import "primereact/resources/themes/saga-green/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "prismjs/themes/prism-coy.css";
import "@fullcalendar/core/main.css";
import "@fullcalendar/daygrid/main.css";
import "@fullcalendar/timegrid/main.css";
import "./layout/flags/flags.css";
import "./layout/layout.scss";
import "./App.scss";

import { useSelector } from "react-redux";
import Login from "./pages/login/Login";
import useMenuItems from "./utilities/menu";
import AllCompanies from "./pages/company/AllCompanies";
import AllUsers from "./pages/user/AllUsers";
import AllGates from "./pages/gate/AllGate";
import Ocr from "./pages/ocr/Ocr";
import GateLogs from "./pages/gateLogs/GateLogs";

const App = () => {
  const [layoutMode] = useState("static");
  const [layoutColorMode] = useState("light");
  const [staticMenuInactive, setStaticMenuInactive] = useState(false);
  const [overlayMenuActive, setOverlayMenuActive] = useState(false);
  const [mobileMenuActive, setMobileMenuActive] = useState(false);
  const [ripple] = useState(true);
  const auth = useSelector((state) => state?.authenticationSlice?.token);
  const menu = useMenuItems();
  const authslice = useSelector((state) => state?.authenticationSlice);
  const userTypeId = authslice?.userTypeId;

  require("dotenv").config();

  const sidebar = useRef();

  const history = useHistory();

  let menuClick = false;
  //  const minutes = 840000;  Refresh Token after 14minutes

  useEffect(() => {
    if (mobileMenuActive) {
      addClass(document.body, "body-overflow-hidden");
    } else {
      removeClass(document.body, "body-overflow-hidden");
    }
  }, [mobileMenuActive]);

  // const onRipple = (e) => {
  //     PrimeReact.ripple = e.value;
  //     setRipple(e.value);
  // };

  // const onLayoutModeChange = (mode) => {
  //     setLayoutMode(mode);
  // };

  // const onColorModeChange = (mode) => {
  //     setLayoutColorMode(mode);
  // };

  const onWrapperClick = (event) => {
    if (!menuClick) {
      setOverlayMenuActive(false);
      setMobileMenuActive(false);
    }
    menuClick = false;
  };

  const onToggleMenu = (event) => {
    menuClick = true;

    if (isDesktop()) {
      if (layoutMode === "overlay") {
        setOverlayMenuActive((prevState) => !prevState);
      } else if (layoutMode === "static") {
        setStaticMenuInactive((prevState) => !prevState);
      }
    } else {
      setMobileMenuActive((prevState) => !prevState);
    }
    event.preventDefault();
  };

  const onSidebarClick = () => {
    menuClick = true;
  };

  const onMenuItemClick = (event) => {
    if (!event.item.items) {
      setOverlayMenuActive(false);
      setMobileMenuActive(false);
    }
  };

  const addClass = (element, className) => {
    if (element.classList) element.classList.add(className);
    else element.className += " " + className;
  };

  const removeClass = (element, className) => {
    if (element.classList) element.classList.remove(className);
    else
      element.className = element.className.replace(
        new RegExp(
          "(^|\\b)" + className.split(" ").join("|") + "(\\b|$)",
          "gi"
        ),
        " "
      );
  };

  const isDesktop = () => {
    return window.innerWidth > 1024;
  };

  const isSidebarVisible = () => {
    if (isDesktop()) {
      if (layoutMode === "static") return !staticMenuInactive;
      else if (layoutMode === "overlay") return overlayMenuActive;
      else return true;
    }

    return true;
  };

  const companyLogo = useSelector(
    (state) => state?.authenticationSlice?.companyLogo
  );

  const wrapperClass = classNames("layout-wrapper", {
    "layout-overlay": layoutMode === "overlay",
    "layout-static": layoutMode === "static",
    "layout-static-sidebar-inactive":
      staticMenuInactive && layoutMode === "static",
    "layout-overlay-sidebar-active":
      overlayMenuActive && layoutMode === "overlay",
    "layout-mobile-sidebar-active": mobileMenuActive,
    "p-ripple-disabled": ripple === false,
  });

  const sidebarClassName = classNames("layout-sidebar", {
    "layout-sidebar-dark": layoutColorMode === "dark",
    "layout-sidebar-light": layoutColorMode === "light",
  });

  const handleLogout = () => {
    if (localStorage.getItem("sessionTimeOut")) {
      localStorage.clear();
      window.location.reload();
      toast.warn("Logged out because of inactivity");
    }
  };

  useIdleTimer({
    timeout: localStorage.getItem("sessionTimeOut") || 600000,
    onIdle: handleLogout,
    debounce: 500,
  });

  return (
    <div className={wrapperClass} onClick={onWrapperClick}>
      <ToastContainer></ToastContainer>
      {auth ? (
        <>
          <AppTopbar onToggleMenu={onToggleMenu} />
          <CSSTransition
            classNames="layout-sidebar"
            timeout={{ enter: 200, exit: 200 }}
            in={isSidebarVisible()}
            unmountOnExit
          >
            <div
              ref={sidebar}
              className={sidebarClassName}
              onClick={onSidebarClick}
            >
              <div
                className="layout-logo"
                style={{ cursor: "pointer" }}
                onClick={() => history.push("/")}
              >
                <img
                  width="50%"
                  alt="Logo"
                  src={`data:image/png;base64,${companyLogo}`}
                />
              </div>
              <AppProfile />
              <AppMenu model={menu} onMenuItemClick={onMenuItemClick} />
            </div>
          </CSSTransition>
          <div className="layout-main">
            <Switch>
              <Route exact path="/" component={Dashboard} />
              <Route exact path="/allcompanies" component={AllCompanies} />
              <Route exact path="/companyusers/:id" component={AllUsers} />
              <Route exact path="/companygates/:id" component={AllGates} />
              <Route exact path="/ocr" component={Ocr} />
              <Route exact path="/gatelogs/:companyId" component={GateLogs} />
            </Switch>
          </div>
        </>
      ) : (
        <div>
          <Switch>
            <Route path="/" exact component={Login} />
            <Redirect to="/" />
          </Switch>
        </div>
      )}
    </div>
  );
};

export default App;
